import store from "services/store";
import api from "services/api";

import Validator from "services/validator";
import {
  Missing,
  isKubernetesName,
  MaxLength,
  ApplyIf,
} from "services/validator/rules";
import createActions from "modules/form/actions";

import {
  awsPolicesFetcher,
  cloudAccountFetcher,
  cloudAccountFormModal,
  CLOUD_ACCOUNTS_MODULE,
  selfHostedOverlordsFetcher,
} from "state/cloudaccounts/services";
import { createAccount } from "state/cloudaccounts/actions/create";
import { getAwsCloudAccountPayload } from "state/cloudaccounts/selectors";

const validator = new Validator();
validator.addRule(["name"], Missing());
validator.addRule(["name"], isKubernetesName());
validator.addRule(["name"], MaxLength(32));

validator.addRule(["accessKey", "secretKey"], (value, key, data) => {
  if (data.credentialType === "secret") {
    return Missing()(value, key, data);
  }
  return false;
});

validator.addRule(["arn", "externalId", "accountId"], (value, key, data) => {
  if (data.credentialType === "sts") {
    return Missing()(value, key, data);
  }
  return false;
});
validator.addRule(
  ["overlordUid"],
  ApplyIf((value, key, data) => !!data?.useSelfHostedPCG, Missing())
);

async function submit(data) {
  const {
    credentialType,
    arn,
    externalId,
    accountId,
    policyARNs,
    enablePolicies,
    partition,
  } = data;
  const commonData = {
    credentialType,
    policyARNs: enablePolicies ? policyARNs : [],
    partition,
  };

  const getCredentials = () => {
    if (credentialType === "sts") {
      return {
        ...commonData,
        sts: {
          arn,
          externalId,
          accountId,
        },
      };
    }

    return {
      ...commonData,
      accessKey: data.accessKey.trim(),
      secretKey: data.secretKey.trim(),
    };
  };

  const payload = {
    metadata: {
      name: data.name,
      annotations: {
        description: data.description,
        overlordUid: data.useSelfHostedPCG ? data.overlordUid : "",
      },
    },
    spec: getCredentials(),
  };

  return store.dispatch(createAccount("aws", payload));
}

async function init() {
  let data;
  let policyARNs = [];
  const stsData = await api.get("v1/clouds/aws/account/sts");

  store.dispatch(selfHostedOverlordsFetcher.fetch());

  if (cloudAccountFormModal.data?.uid) {
    data = await store.dispatch(cloudAccountFetcher.fetch());
    store.dispatch({
      type: "SET_ACCOUNT_AS_VALID",
    });

    const payload = {
      secret: {
        accessKey: data?.spec?.accessKey,
        secretKey: data?.spec?.secretKey,
        partition: data?.spec?.partition,
      },
      sts: {
        credentialType: data?.spec?.credentialType,
        sts: {
          arn: data?.spec?.sts?.arn,
          externalId: stsData?.accountId,
          accountId: stsData?.externalId,
        },
        partition: data?.spec?.partition,
      },
    };

    policyARNs = [...(data?.spec?.policyARNs || [])];

    if (policyARNs.length > 0) {
      store.dispatch(
        awsPolicesFetcher.fetch({
          cloudAccountUid: cloudAccountFormModal.data.uid,
          payload,
        })
      );
    }
  }

  return Promise.resolve({
    name: data?.metadata?.name || "",
    accessKey: data?.spec?.accessKey || "",
    secretKey: data?.spec?.secretKey || "",
    description: data?.metadata?.annotations?.description || "",
    credentialType: data?.spec?.credentialType || "secret",
    arn: data?.spec?.sts?.arn || "",
    accountId: stsData?.accountId,
    externalId: stsData?.externalId,
    policyARNs,
    enablePolicies: !!policyARNs.length > 0,
    partition: data?.spec?.partition || "aws",
    overlordUid: data?.metadata?.annotations?.overlordUid,
    useSelfHostedPCG: !!data?.metadata?.annotations?.overlordUid,
  });
}

export function onIAMPoliciesToggle(value) {
  return (dispatch, getState) => {
    dispatch(
      awsAccountFormActions.onChange({
        name: "enablePolicies",
        value,
        module: CLOUD_ACCOUNTS_MODULE,
      })
    );

    if (value) {
      const payload = getAwsCloudAccountPayload(getState());
      dispatch(
        awsPolicesFetcher.fetch({
          payload,
          cloudAccountUid: cloudAccountFormModal?.data?.uid,
        })
      );
    }
  };
}

export function onPartitionFieldChange(value) {
  return async (dispatch) => {
    dispatch(
      awsAccountFormActions.onChange({
        name: "partition",
        value,
        module: CLOUD_ACCOUNTS_MODULE,
      })
    );

    const stsData = await api.get("v1/clouds/aws/account/sts", {
      partition: value,
    });

    dispatch(
      awsAccountFormActions.batchChange({
        module: CLOUD_ACCOUNTS_MODULE,
        updates: {
          accountId: stsData?.accountId,
          externalId: stsData?.externalId,
        },
      })
    );
  };
}

export const awsAccountFormActions = createActions({
  submit,
  validator,
  init,
});
